<script setup lang="ts">
import AddNewUserDrawer from '@/views/apps/user/list/AddNewUserDrawer.vue'
import type { UserProperties } from '@/views/apps/user/types'
import { sort } from '@formkit/drag-and-drop'
import type { DataTableHeader } from 'vuetify'

// DAY-37: only for users whose roles allow it (same permission the API checks)
definePage({
  meta: {
    action: 'read',
    subject: 'Participants',
  },
})


const { t } = useI18n()

// 👉 Store
const searchQuery = ref('')
const selectedFilter = ref()

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()
const selectedRows = ref([])
const isActive = ref(true)
const memberStatus = ref()
const isFavourite = ref()

// Update data table options
const updateOptions = (options: any) => {
  if (options.sortBy[0]) {
    sortBy.value = options.sortBy[0].key
    orderBy.value = options.sortBy[0].order
  }
}

// Headers
const headers: DataTableHeader[] = [
  { title: 'User', key: 'firstName' },
  { title: 'Date of Birth', key: 'dateOfBirth' },
  { title: 'Status', key: 'status' },
  { title: 'Member ID', key: 'id', sortable: false, width: 120 } as DataTableHeader,
  { title: 'Actions', key: 'actions', sortable: false, align: 'end', width: 56 } as DataTableHeader,
]

// 👉 Fetching users
const {
  data: usersData,
  execute: fetchUsers,
  isFetching,
} = await useApi<any>(
  createUrl('/Member/GetAllMembersSorted', {
    query: {
      pageNumber: page,
      pageSize: itemsPerPage,
      sortBy,
      sortDirection: orderBy,
      searchTerm: searchQuery,
      isActive,
      memberStatus,
      isFavourite,
    },
  })
)

const users = computed((): UserProperties[] => usersData.value.data.map(({ member }: { member: UserProperties }) => member))
const totalUsers = computed(() => usersData.value.totalCount)

// 👉 search filters
const filters = [
  // { title: 'Most Active Members', value: 'active' },
  // { title: 'Recently Updated', value: 'updated' },
  { title: 'Inactive Members', value: 'inactive' },
  { title: 'Discharged Recently', value: 'discharged' },
]

const resolveUserRoleVariant = (role: string) => {
  const roleLowerCase = role.toLowerCase()

  if (roleLowerCase === 'subscriber') return { color: 'success', icon: 'bx-user' }
  if (roleLowerCase === 'author') return { color: 'error', icon: 'bx-desktop' }
  if (roleLowerCase === 'maintainer') return { color: 'info', icon: 'bx-pie-chart-alt' }
  if (roleLowerCase === 'editor') return { color: 'warning', icon: 'bx-edit' }
  if (roleLowerCase === 'admin') return { color: 'primary', icon: 'bx-crown' }

  return { color: 'primary', icon: 'bx-user' }
}

const resolveUserStatusVariant = (stat: string) => {
  const statLowerCase = stat.toLowerCase()
  if (statLowerCase === 'pending') return 'warning'
  if (statLowerCase === 'active') return 'success'
  if (statLowerCase === 'inactive') return 'secondary'

  return 'primary'
}

watch(selectedFilter, (newFilter) => {
  switch (newFilter) {
    case 'active':
      isActive.value = true
      orderBy.value = 'desc'
      sortBy.value = undefined
      memberStatus.value = 'Active'
      break
    case 'updated':
      isActive.value = true
      sortBy.value = 'statusUpdatedOn'
      memberStatus.value = 'Active'
      orderBy.value = 'asc'
      break
    case 'inactive':
      isActive.value = false
      memberStatus.value = 'Inactive'
      sortBy.value = undefined
      orderBy.value = 'asc'
      break
    case 'discharged':
      isActive.value = false
      memberStatus.value = 'Discharged'
      sortBy.value = undefined
      orderBy.value = 'asc'
      break
    default:
      isActive.value = true
      memberStatus.value = 'Active'
      sortBy.value = undefined
      orderBy.value = 'asc'
  }
})

const isAddNewUserDrawerVisible = ref(false)

// 👉 Add new user
const addNewUser = async (userData: UserProperties) => {
  await $api('/Member/CreateMember', {
    method: 'POST',
    body: userData,
  })

  // Refetch User
  fetchUsers()
}

// 👉 Delete user — soft-deletes via the API. Triggered only after the
// user confirms in the dialog below; the kebab item just opens the prompt.
const isDeleteDialogVisible = ref(false)
const userToDeleteId = ref<number | null>(null)

const promptDeleteUser = (id: number) => {
  userToDeleteId.value = id
  isDeleteDialogVisible.value = true
}

const cancelDeleteUser = () => {
  isDeleteDialogVisible.value = false
  userToDeleteId.value = null
}

const confirmDeleteUser = async () => {
  const id = userToDeleteId.value
  isDeleteDialogVisible.value = false
  userToDeleteId.value = null
  if (id == null) return

  await $api(`/Member/DeleteMember/${id}`, { method: 'DELETE' })

  const index = selectedRows.value.findIndex(row => row === id)
  if (index !== -1) selectedRows.value.splice(index, 1)

  fetchUsers()
}

// 👉 Reactivate user — only offered while the list shows inactive members.
// Like Delete, the kebab item opens a confirmation dialog first.
const snackbar = useSnackbar()
const isReactivateDialogVisible = ref(false)
const userToReactivateId = ref<number | null>(null)

const promptReactivateUser = (id: number) => {
  userToReactivateId.value = id
  isReactivateDialogVisible.value = true
}

const cancelReactivateUser = () => {
  isReactivateDialogVisible.value = false
  userToReactivateId.value = null
}

const confirmReactivateUser = async () => {
  const id = userToReactivateId.value
  isReactivateDialogVisible.value = false
  userToReactivateId.value = null
  if (id == null) return

  try {
    await $api(`/Member/ReactivateMember/${id}`, { method: 'PUT' })
  }
  catch (err: any) {
    snackbar.error(err?.statusCode === 403
      ? t('You do not have permission to reactivate this user.')
      : t('Reactivation failed. Please try again.'))

    return
  }

  const index = selectedRows.value.findIndex(row => row === id)
  if (index !== -1) selectedRows.value.splice(index, 1)

  fetchUsers()
}

const convertISOdate = (isoDate: string) => {
  const date = new Date(isoDate)
  return date.toLocaleDateString('en-US')
}

const widgetData = ref([
  { title: 'Session', value: totalUsers, change: 29, desc: 'Total Users', icon: 'bx-group', iconColor: 'primary' },
  { title: 'Paid Users', value: totalUsers, change: 18, desc: 'Last Week Analytics', icon: 'bx-user-plus', iconColor: 'error' },
  { title: 'Active Users', value: totalUsers, change: -14, desc: 'Last Week Analytics', icon: 'bx-user-check', iconColor: 'success' },
  { title: 'Pending Users', value: '0', change: 42, desc: 'Last Week Analytics', icon: 'bx-user-voice', iconColor: 'warning' },
])
</script>

<template>
  <section>
    <!-- 👉 Widgets -->
    <div class="d-flex mb-6">
      <VRow>
        <template v-for="(data, id) in widgetData" :key="id">
          <VCol cols="12" md="3" sm="6">
            <VCard>
              <VCardText>
                <div class="d-flex justify-space-between">
                  <div class="d-flex flex-column gap-y-1">
                    <div class="text-body-1 text-high-emphasis">
                      {{ data.title }}
                    </div>
                    <div class="d-flex gap-x-2 align-center">
                      <h4 class="text-h4">
                        {{ data.value }}
                      </h4>
                      <div class="text-base" :class="data.change > 0 ? 'text-success' : 'text-error'">
                        ({{ prefixWithPlus(data.change) }}%)
                      </div>
                    </div>
                    <div class="text-sm">
                      {{ data.desc }}
                    </div>
                  </div>
                  <VAvatar :color="data.iconColor" variant="tonal" rounded size="40">
                    <VIcon :icon="data.icon" size="24" />
                  </VAvatar>
                </div>
              </VCardText>
            </VCard>
          </VCol>
        </template>
      </VRow>
    </div>

    <VCard class="mb-6">
      <VCardItem class="pb-4">
        <VCardTitle>Filters</VCardTitle>
      </VCardItem>

      <VCardText>
        <VRow>
          <!-- 👉 Select Filter -->
          <VCol cols="12" sm="4">
            <AppSelect v-model="selectedFilter" placeholder="Select Filter" :items="filters" clearable clear-icon="bx-x" />
          </VCol>
        </VRow>
      </VCardText>

      <VDivider />

      <VCardText class="d-flex flex-wrap gap-4">
        <div class="me-3 d-flex gap-3">
          <AppSelect
            :model-value="itemsPerPage"
            :items="[
              { value: 10, title: '10' },
              { value: 25, title: '25' },
              { value: 50, title: '50' },
              { value: 100, title: '100' },
              { value: -1, title: 'All' },
            ]"
            style="inline-size: 6.25rem"
            @update:model-value="itemsPerPage = parseInt($event, 10)" />
        </div>
        <VSpacer />

        <div class="app-user-search-filter d-flex align-center flex-wrap gap-4">
          <!-- 👉 Search  -->
          <div style="inline-size: 15.625rem">
            <AppTextField v-model="searchQuery" placeholder="Search User" />
          </div>

          <!-- 👉 Export button -->
          <VBtn variant="tonal" color="secondary" prepend-icon="bx-export"> Export </VBtn>

          <!-- 👉 Add user button -->
          <VBtn prepend-icon="bx-plus" @click="isAddNewUserDrawerVisible = true"> Add New User </VBtn>
        </div>
      </VCardText>
      <VDivider />

      <!-- SECTION datatable -->
      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:model-value="selectedRows"
        v-model:page="page"
        :items="users"
        item-value="id"
        :items-length="totalUsers"
        :headers="headers"
        class="text-no-wrap"
        show-select
        :loading="isFetching"
        @update:options="updateOptions">
        <!-- User -->
        <template #item.firstName="{ item }">
          <div class="d-flex align-center gap-x-4">
            <VAvatar
              size="34"
              :variant="!item.profilePictureUrl ? 'tonal' : undefined"
              :color="!item.profilePictureUrl ? resolveUserRoleVariant('admin').color : undefined">
              <VImg v-if="item.profilePictureUrl" :src="item.profilePictureUrl" />
              <span v-else>{{ avatarText(item.firstName + ' ' + item.lastName) }}</span>
            </VAvatar>
            <div class="d-flex flex-column">
              <h6 class="text-base">
                <RouterLink :to="{ name: 'apps-user-view-id', params: { id: item.id } }" class="font-weight-medium text-link">
                  {{ item.firstName + ' ' + item.lastName }}
                </RouterLink>
              </h6>
              <div class="text-sm">
                {{ item.email }}
              </div>
            </div>
          </div>
        </template>

        <!-- 👉 Role -->
        <template #item.dateOfBirth="{ item }">
          <div class="d-flex align-center gap-x-2">
            <div class="text-capitalize text-high-emphasis text-body-1">
              {{ convertISOdate(item.dateOfBirth) }}
            </div>
          </div>
        </template>

        <!-- ID -->
        <template #item.id="{ item }">
          <div class="text-body-1 text-high-emphasis text-capitalize">
            {{ '#' + item.id }}
          </div>
        </template>

        <!-- Status -->
        <template #item.status="{ item }">
          <VChip :color="resolveUserStatusVariant(item.isActive ? 'active' : 'inactive')" size="small" label class="text-capitalize">
            {{ item.isActive ? 'Active' : 'Inactive' }}
          </VChip>
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <VBtn icon variant="text" color="medium-emphasis">
            <VIcon icon="bx-dots-vertical-rounded" />
            <VMenu activator="parent">
              <VList>
                <VListItem :to="{ name: 'apps-user-view-id', params: { id: item.id } }">
                  <template #prepend>
                    <VIcon icon="bx-show" />
                  </template>
                  <VListItemTitle>{{ t('View') }}</VListItemTitle>
                </VListItem>

                <VListItem :to="{ name: 'apps-user-view-id', params: { id: item.id }, query: { edit: 'all' } }">
                  <template #prepend>
                    <VIcon icon="bx-pencil" />
                  </template>
                  <VListItemTitle>{{ t('Edit') }}</VListItemTitle>
                </VListItem>

                <VListItem v-if="memberStatus === 'Inactive'" @click="promptReactivateUser(item.id)">
                  <template #prepend>
                    <VIcon icon="bx-revision" color="success" />
                  </template>
                  <VListItemTitle class="text-success">
                    {{ t('Reactivate') }}
                  </VListItemTitle>
                </VListItem>

                <VListItem v-else @click="promptDeleteUser(item.id)">
                  <template #prepend>
                    <VIcon icon="bx-trash" color="error" />
                  </template>
                  <VListItemTitle class="text-error">
                    {{ t('Delete') }}
                  </VListItemTitle>
                </VListItem>
              </VList>
            </VMenu>
          </VBtn>
        </template>

        <!-- pagination -->
        <template #bottom>
          <TablePagination v-model:page="page" :items-per-page="itemsPerPage" :total-items="totalUsers" />
        </template>
      </VDataTableServer>
      <!-- SECTION -->
    </VCard>
    <!-- 👉 Add New User -->
    <AddNewUserDrawer v-model:is-drawer-open="isAddNewUserDrawerVisible" @user-data="addNewUser" />

    <!-- 👉 Delete confirmation -->
    <VDialog
      v-model="isDeleteDialogVisible"
      max-width="480"
      @update:model-value="(v: boolean) => { if (!v) cancelDeleteUser() }"
    >
      <VCard>
        <VCardTitle class="d-flex align-center pe-2">
          {{ t('Confirm Deletion') }}
          <VSpacer />
          <VBtn icon variant="text" size="small" @click="cancelDeleteUser">
            <VIcon icon="bx-x" />
          </VBtn>
        </VCardTitle>

        <VDivider />

        <VCardText class="pt-4">
          {{ t('This user will be marked as Inactive, are you sure you want to continue?') }}
        </VCardText>

        <VCardActions class="px-4 pb-4">
          <VSpacer />
          <VBtn variant="tonal" color="secondary" @click="cancelDeleteUser">
            {{ t('No') }}
          </VBtn>
          <VBtn color="error" @click="confirmDeleteUser">
            {{ t('Yes') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 👉 Reactivate confirmation -->
    <VDialog
      v-model="isReactivateDialogVisible"
      max-width="480"
      @update:model-value="(v: boolean) => { if (!v) cancelReactivateUser() }"
    >
      <VCard>
        <VCardTitle class="d-flex align-center pe-2">
          {{ t('Confirm Reactivation') }}
          <VSpacer />
          <VBtn icon variant="text" size="small" @click="cancelReactivateUser">
            <VIcon icon="bx-x" />
          </VBtn>
        </VCardTitle>

        <VDivider />

        <VCardText class="pt-4">
          {{ t('This user will be marked as Active, are you sure you want to continue?') }}
        </VCardText>

        <VCardActions class="px-4 pb-4">
          <VSpacer />
          <VBtn variant="tonal" color="secondary" @click="cancelReactivateUser">
            {{ t('No') }}
          </VBtn>
          <VBtn color="success" @click="confirmReactivateUser">
            {{ t('Yes') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>
